import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import ScrollTop from "@/components/feature/ScrollTop";
import ContactForm from "@/components/feature/ContactForm";
import { useSeo } from "@/hooks/useSeo";
import { videos, categories, hotTopics, faqs } from "@/mocks/videos";

const testimonials = [
  { name: "一只爱吃瓜的猫", role: "普通网友", text: "每天上班路上刷一刷，娱乐圈的新鲜事基本都能第一时间看到，更新真的很快。" },
  { name: "小夏", role: "综艺爱好者", text: "综艺片段和名场面整理得特别全，省得我自己去一集集找，非常实用。" },
  { name: "北方的风", role: "影视观众", text: "最打动我的是影视花絮板块，能看到很多银幕背后真实的剧组日常，内容有温度。" },
];

function SectionHeader({ title, sub, link }: { title: string; sub: string; link?: { to: string; label: string } }) {
  return (
    <div className="flex items-end justify-between mb-6">
      <div>
        <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 flex items-center gap-2">
          <span className="w-1.5 h-6 rounded bg-primary-500"></span>
          {title}
        </h2>
        <p className="mt-1 text-sm text-foreground-500">{sub}</p>
      </div>
      {link && (
        <Link to={link.to} className="whitespace-nowrap text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
          {link.label}<i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  useSeo({
    title: "吃瓜视频网 - 娱乐八卦与圈内速递，明星动态、综艺片段、影视花絮一站看全",
    description: "吃瓜视频网收录全网娱乐八卦、圈内速递、明星动态、综艺片段、影视花絮与红人视频，每日更新娱乐圈新鲜事，带你一分钟掌握圈内动态。",
    keywords: "吃瓜视频网,娱乐八卦,圈内速递,明星动态,综艺片段,影视花絮,红人视频",
  });

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.textContent = JSON.stringify(schema);
    document.head.appendChild(tag);
    return () => {
      document.head.removeChild(tag);
    };
  }, []);

  const featured = videos[0];
  const latest = videos.slice(0, 8);
  const hotPicks = videos.filter((v) => v.hot);
  const starNews = videos.filter((v) => v.category === "star").slice(0, 3);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=glamorous%20entertainment%20red%20carpet%20gala%20with%20warm%20golden%20spotlights%2C%20crowd%20and%20stage%20lights%20bokeh%20in%20background%2C%20cinematic%20editorial%20photography%2C%20festive%20warm%20atmosphere%2C%20dramatic%20lighting&width=1600&height=900&seq=hero-chigua-main&orientation=landscape"
          alt="吃瓜视频网娱乐红毯盛典"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="relative h-full w-full flex items-center justify-center text-center">
          <div className="max-w-3xl w-full px-4 text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-bold">
              <i className="ri-flashlight-line"></i>今日头条 · 圈内速递
            </span>
            <h1 className="mt-4 text-2xl md:text-4xl font-heading text-background-50 leading-snug">
              吃瓜视频网 —— 娱乐圈新鲜事，一站看全
            </h1>
            <p className="mt-3 text-sm md:text-base text-background-50/90 max-w-xl mx-auto">
              娱乐八卦、明星动态、综艺片段、影视花絮与红人视频每日更新，理性吃瓜，快乐追剧。
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={`/detail/${featured.slug}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors"
              >
                <i className="ri-play-circle-line text-lg"></i>立即开吃
              </Link>
              <Link
                to="/category/gossip"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50/15 backdrop-blur text-background-50 text-sm font-bold border border-background-50/40 hover:bg-background-50/25 transition-colors"
              >
                <i className="ri-compass-line text-lg"></i>浏览娱乐八卦
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trending topics */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-8 md:mt-12">
        <SectionHeader title="今日热搜" sub="看看大家都在吃谁的瓜" />
        <div className="bg-background-100 border border-background-200 rounded-lg p-4 md:p-5">
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {hotTopics.map((t, i) => (
              <li key={t} className="flex items-center gap-2.5 bg-background-50 border border-background-200 rounded-md px-3 py-2.5">
                <span className={`text-sm font-bold w-5 text-center ${i < 3 ? "text-primary-500" : "text-foreground-400"}`}>{i + 1}</span>
                <Link to={`/search?q=${encodeURIComponent(t)}`} className="flex-1 text-sm text-foreground-800 hover:text-primary-600 truncate">
                  {t}
                </Link>
                <i className="ri-fire-fill text-accent-500"></i>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-10 md:mt-14">
        <SectionHeader title="内容栏目" sub="按你感兴趣的方向快速进入" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group bg-background-100 border border-background-200 rounded-lg p-5 hover:-translate-y-1 transition-transform"
            >
              <span className="w-11 h-11 rounded-lg bg-primary-500/10 text-primary-600 flex items-center justify-center text-2xl group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className={c.icon}></i>
              </span>
              <h3 className="mt-3 text-base font-bold text-foreground-950">{c.name}</h3>
              <p className="mt-1 text-xs text-foreground-600 leading-relaxed line-clamp-2">{c.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest videos */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-10 md:mt-14">
        <SectionHeader title="最新吃瓜" sub="刚刚更新的圈内动态" link={{ to: "/category/gossip", label: "查看更多" }} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {latest.map((v) => (
            <VideoCard key={v.slug} video={v} />
          ))}
        </div>
      </section>

      {/* Hot picks horizontal scroll */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-10 md:mt-14">
        <SectionHeader title="热门看点" sub="播放量爆表的高能片段" />
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
          {hotPicks.map((v) => (
            <div key={v.slug} className="w-72 shrink-0">
              <VideoCard video={v} compact />
            </div>
          ))}
        </div>
      </section>

      {/* Star news + testimonials side */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-10 md:mt-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* star news */}
          <div className="lg:col-span-2 bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-2xl text-foreground-950 flex items-center gap-2">
                <i className="ri-star-smile-line text-accent-500"></i>明星动态快报
              </h2>
              <Link to="/category/star" className="whitespace-nowrap text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
                全部动态<i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="space-y-4">
              {starNews.map((v) => (
                <Link key={v.slug} to={`/detail/${v.slug}`} className="group flex gap-4 items-center bg-background-50 border border-background-200 rounded-lg p-3 hover:border-primary-300 transition-colors">
                  <img src={v.cover} alt={v.title} className="w-28 md:w-36 h-20 object-cover rounded-md shrink-0" loading="lazy" />
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">{v.title}</h3>
                    <p className="mt-1 text-xs text-foreground-500 flex items-center gap-2">
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{v.views}</span>
                      <span>{v.date}</span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* testimonials */}
          <aside className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-2xl text-foreground-950 flex items-center gap-2 mb-5">
              <i className="ri-chat-heart-line text-primary-500"></i>网友口碑
            </h2>
            <div className="space-y-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="bg-background-50 border border-background-200 rounded-lg p-4">
                  <div className="flex items-center gap-1 text-accent-500 text-sm">
                    <i className="ri-star-fill"></i><i className="ri-star-fill"></i><i className="ri-star-fill"></i><i className="ri-star-fill"></i><i className="ri-star-fill"></i>
                  </div>
                  <blockquote className="mt-2 text-sm text-foreground-700 leading-relaxed">“{t.text}”</blockquote>
                  <figcaption className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
                    <span className="w-7 h-7 rounded-full bg-secondary-500/15 text-secondary-700 flex items-center justify-center text-sm"><i className="ri-user-line"></i></span>
                    <span className="font-bold text-foreground-800">{t.name}</span>
                    <span>· {t.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-10 md:mt-14">
        <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-8">
          <SectionHeader title="常见问题" sub="关于吃瓜视频网的解答" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {faqs.map((f) => (
              <details key={f.q} className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between gap-3 cursor-pointer px-4 py-3.5 text-sm font-bold text-foreground-950 list-none">
                  <span>{f.q}</span>
                  <i className="ri-add-line text-lg text-primary-600 group-open:rotate-45 transition-transform shrink-0"></i>
                </summary>
                <p className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="mx-auto max-w-7xl px-4 md:px-6 mt-10 md:mt-14 scroll-mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start bg-background-100 border border-background-200 rounded-lg p-5 md:p-8">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500/15 text-accent-700 text-xs font-bold">
              <i className="ri-edit-2-line"></i>投稿爆料 · 联系我们
            </span>
            <h2 className="mt-4 font-heading text-2xl md:text-3xl text-foreground-950">有圈内线索？欢迎投稿</h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              无论你是想爆料圈内趣事、反馈内容建议，还是洽谈合作，都可以通过右侧表单与我们联系。我们会认真阅读每一条留言，并尽快给予回复。
            </p>
            <div className="mt-6 space-y-3 text-sm text-foreground-700">
              <p className="flex items-center gap-2"><i className="ri-mail-line text-primary-600"></i>contact@chigua-video.example.com</p>
              <p className="flex items-center gap-2"><i className="ri-time-line text-primary-600"></i>工作日 09:00 - 21:00 在线回复</p>
            </div>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
            <ContactForm />
          </div>
        </div>
      </section>

      <Footer />
      <ScrollTop />
    </div>
  );
}